import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * PipelineTracker
 * Horizontal ATS-style pipeline visualization.
 * Read-only — visualizes applicant.pipeline_stage and applicant.stage_updated_at.
 * Does not touch the existing `status` field or any existing functionality.
 */

const STAGES = [
  { key: 'applied', label: 'Applied' },
  { key: 'resume_review', label: 'Resume Review' },
  { key: 'screening', label: 'AI & HR Screening' },
  { key: 'shortlisted', label: 'Shortlisted' },
  { key: 'technical_interview', label: 'Technical Interview' },
  { key: 'manager_interview', label: 'Manager Interview' },
  { key: 'final_hr', label: 'Final HR' },
  { key: 'offer_accepted', label: 'Offer Accepted' },
  { key: 'background_verification', label: 'Background Verification' },
  { key: 'joined', label: 'Joined' }
];

const FLAG_STYLES = {
  rejected: { label: 'Rejected', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', border: 'border-red-200' },
  on_hold: { label: 'On Hold', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', border: 'border-orange-200' },
  dropped: { label: 'Dropped', color: 'bg-gray-500', textColor: 'text-gray-700', bgLight: 'bg-gray-50', border: 'border-gray-300' },
  withdrawn: { label: 'Withdrawn', color: 'bg-gray-400', textColor: 'text-gray-600', bgLight: 'bg-gray-50', border: 'border-gray-300' }
};

const daysSince = (dateStr) => {
  if (!dateStr) return 0;
  const then = new Date(dateStr.replace(' ', 'T'));
  const now = new Date();
  const diffMs = now - then;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

const normalizeStage = (key) => {
  const k = (key || '').toLowerCase().trim();
  if (k === 'ai_screening' || k === 'hr_screening' || k === 'screening') return 'screening';
  return k;
};

const PipelineTracker = ({
  currentStage = 'applied',
  stageUpdatedAt,
  activeFlag = null,
  applicant = null,
  interviews = [],
  screeningDetails = null,
  onStageClick
}) => {
  const normalizedCurrent = normalizeStage(currentStage);
  const currentIndex = STAGES.findIndex((s) => s.key === normalizedCurrent);
  const days = daysSince(stageUpdatedAt);

  const aiStatus = (
    screeningDetails?.ai_screening_status ||
    screeningDetails?.aiScreeningStatus ||
    applicant?.ai_screening_status ||
    applicant?.aiScreeningStatus ||
    ''
  ).toUpperCase().trim();

  const aiResult = (
    screeningDetails?.ai_screening_result ||
    screeningDetails?.aiScreeningResult ||
    applicant?.ai_screening_result ||
    applicant?.aiScreeningResult ||
    ''
  ).toUpperCase().trim();

  const aiScore = screeningDetails?.ai_screening_score ?? applicant?.ai_screening_score;
  const hasAiEval = Boolean(screeningDetails?.evaluation || applicant?.ai_screening_evaluation);
  const aiCompletedAt = screeningDetails?.ai_screening_completed_at || applicant?.ai_screening_completed_at;

  const hrStatus = (applicant?.hr_screening_status || screeningDetails?.hr_screening_status || '').toLowerCase();
  const hrScore = applicant?.hr_screening_score ?? screeningDetails?.hr_screening_score;
  const hasHrScore = hrScore !== null && hrScore !== undefined && hrScore !== '';
  const hrPassed = ['pass', 'cleared', 'passed'].includes(hrStatus) || (hasHrScore && Number(hrScore) >= 70);

  const completedStatuses = ['COMPLETED', 'PASSED', 'EVALUATED', 'SHORTLISTED', 'RECOMMENDED', 'HIRE', 'STRONG HIRE'];
  const aiDone = (currentIndex > 2) ||
                 completedStatuses.includes(aiStatus) ||
                 Boolean(aiCompletedAt) ||
                 (aiScore !== null && aiScore !== undefined && aiScore !== '') ||
                 hasAiEval;
  const hrDone = (currentIndex > 2) || hrPassed || (hasHrScore && Number(hrScore) >= 50);

  // Find relevant interview records
  const internalInt = (interviews || []).find((i) => i.stage === 'internal' || i.stage === 'technical_interview');
  const externalInt = (interviews || []).find((i) => i.stage === 'external' || i.stage === 'manager_interview');

  const dayBadgeColor =
    days > 10 ? 'bg-red-100 text-red-700 border-red-300' :
    days > 5 ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
    'bg-muted text-muted-foreground border-border';

  if (activeFlag && FLAG_STYLES[activeFlag.flag_type]) {
    const style = FLAG_STYLES[activeFlag.flag_type];
    const stageLabel = STAGES[currentIndex]?.label || currentStage;
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${style.bgLight} ${style.border} w-fit`}>
        <Icon name={activeFlag.flag_type === 'rejected' ? 'XCircle' : activeFlag.flag_type === 'on_hold' ? 'PauseCircle' : 'AlertCircle'} size={16} className={style.textColor} />
        <span className={`text-sm font-medium ${style.textColor}`}>
          {style.label} at {stageLabel}
        </span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Hiring Pipeline</span>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
            <Icon name="Sparkles" size={12} />
            Pipeline Gated & Verified
          </span>
          {normalizedCurrent === 'screening' && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
              aiStatus === 'IN_PROGRESS' ? 'bg-amber-500/10 text-amber-700 border-amber-500/30 animate-pulse' :
              (aiResult === 'FAIL' || hrStatus === 'fail' || hrStatus === 'rejected') ? 'bg-red-500/10 text-red-700 border-red-500/30' :
              'bg-blue-500/10 text-blue-700 border-blue-500/30'
            }`}>
              <Icon name="PhoneCall" size={11} />
              AI & HR Screening: {
                (aiDone && hrDone) ? 'Completed' :
                aiStatus === 'IN_PROGRESS' ? 'AI In Progress' :
                aiDone ? 'AI Passed / HR Pending' :
                hrDone ? 'HR Passed / AI Pending' :
                (aiResult === 'FAIL' || hrStatus === 'fail') ? 'Failed' : 'Pending'
              }
            </span>
          )}
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${dayBadgeColor}`}>
          {days} {days === 1 ? 'day' : 'days'} in stage
        </span>
      </div>

      <div className="overflow-x-auto pb-2 -mx-1 px-1">
        <div className="flex items-center min-w-max">
          {STAGES.map((stage, index) => {
            let isCompleted = false;
            let isFailed = false;
            let isCurrent = index === currentIndex;

            switch (stage.key) {
              case 'applied':
                isCompleted = true;
                break;

              case 'resume_review':
                if (applicant?.ats_score !== null && applicant?.ats_score !== undefined && applicant?.ats_score !== '') {
                  if (Number(applicant.ats_score) >= 60 || applicant?.resume_url) {
                    isCompleted = true;
                  } else {
                    isFailed = true;
                  }
                } else if (applicant?.resume_url || index < currentIndex) {
                  isCompleted = true;
                } else {
                  isCompleted = false;
                }
                break;

              case 'screening':
                if (currentIndex > 2 || (aiDone && hrDone)) {
                  isCompleted = true;
                } else if (aiResult === 'FAIL' || aiStatus === 'EXPIRED' || ['fail', 'rejected', 'failed', 'expired'].includes(hrStatus)) {
                  isFailed = true;
                } else if (index < currentIndex) {
                  isCompleted = true;
                } else {
                  isCompleted = false;
                }
                break;

              case 'shortlisted':
                const shortStatus = (applicant?.shortlisted_status || '').toLowerCase();
                const parentHrPassed = ['pass', 'cleared', 'passed'].includes((applicant?.hr_screening_status || screeningDetails?.hr_screening_status || '').toLowerCase()) ||
                                      ( (applicant?.hr_screening_score ?? screeningDetails?.hr_screening_score) !== null && Number(applicant?.hr_screening_score ?? screeningDetails?.hr_screening_score) >= 70 );
                if (['pass', 'cleared', 'passed', 'shortlisted'].includes(shortStatus) || parentHrPassed) {
                  isCompleted = true;
                } else if (['fail', 'rejected', 'failed'].includes(shortStatus)) {
                  isFailed = true;
                } else if (index < currentIndex) {
                  isCompleted = true;
                } else {
                  isCompleted = false;
                }
                break;

              case 'technical_interview':
                if (internalInt) {
                  const res = (internalInt.result || '').toLowerCase();
                  if (res === 'pass') isCompleted = true;
                  else if (res === 'fail') isFailed = true;
                  else isCompleted = false;
                } else {
                  isCompleted = false;
                }
                break;

              case 'manager_interview':
                if (externalInt) {
                  const res = (externalInt.result || '').toLowerCase();
                  if (res === 'pass') isCompleted = true;
                  else if (res === 'fail') isFailed = true;
                  else isCompleted = false;
                } else {
                  isCompleted = false;
                }
                break;

              case 'final_hr':
                const finalHrStatus = (applicant?.final_hr_status || '').toLowerCase();
                const finalHrOutcome = (applicant?.final_hr_outcome || '').toLowerCase();
                
                // Check outcome first (takes precedence)
                if (finalHrOutcome === 'selected') {
                  isCompleted = true;
                } else if (finalHrOutcome === 'rejected') {
                  isFailed = true;
                } else if (['cleared', 'pass', 'passed', 'selected'].includes(finalHrStatus) || (applicant?.offered_ctc && applicant?.offer_letter_id)) {
                  isCompleted = true;
                } else if (['fail', 'rejected', 'failed'].includes(finalHrStatus)) {
                  isFailed = true;
                } else {
                  isCompleted = false;
                }
                break;

              case 'offer_accepted':
                const offerStatus = (applicant?.offer_status || applicant?.final_hr_status || '').toLowerCase();
                const currentStageRaw = (applicant?.pipeline_stage || '').toLowerCase();

                if (currentStageRaw === 'offer_rejected' || ['rejected', 'failed', 'declined'].includes(offerStatus)) {
                  isFailed = true;
                } else if (['offer_accepted', 'background_verification', 'joined'].includes(currentStageRaw) || ['cleared', 'accepted', 'passed', 'selected'].includes(offerStatus) || index < currentIndex) {
                  isCompleted = true;
                } else {
                  isCompleted = false;
                }
                break;

              case 'background_verification':
                const bgStatus = (applicant?.bg_verification_status || applicant?.background_verification_status || '').toLowerCase();
                const currentStageRawBg = (applicant?.pipeline_stage || '').toLowerCase();

                if (currentStageRawBg === 'background_verification_failed' || ['failed', 'rejected', 'fail'].includes(bgStatus)) {
                  isFailed = true;
                } else if (['joined'].includes(currentStageRawBg) || ['passed', 'cleared', 'pass'].includes(bgStatus) || index < currentIndex) {
                  isCompleted = true;
                } else {
                  isCompleted = false;
                }
                break;

              case 'joined':
                const joinedStatus = (applicant?.joined_status || applicant?.onboarding_status || '').toLowerCase();
                const currentStageRawJoined = (applicant?.pipeline_stage || '').toLowerCase();

                if (currentStageRawJoined === 'not_joined' || currentStageRawJoined === 'joined_failed' || ['not_joined', 'no_show', 'failed', 'rejected'].includes(joinedStatus)) {
                  isFailed = true;
                } else if (currentStageRawJoined === 'joined' || ['joined', 'onboarded', 'passed', 'cleared'].includes(joinedStatus)) {
                  isCompleted = true;
                } else {
                  isCompleted = false;
                }
                break;

              default:
                isCompleted = index < currentIndex;
                break;
            }

            if (stage.key === currentStage) {
              if (!isCompleted && !isFailed) {
                isCurrent = true;
              } else {
                isCurrent = false;
              }
            } else {
              isCurrent = false;
            }

            const isUpcoming = !isCompleted && !isFailed && !isCurrent;

            return (
              <React.Fragment key={stage.key}>
                <button
                  type="button"
                  onClick={() => onStageClick && onStageClick(stage, index)}
                  disabled={!onStageClick}
                  className={`flex flex-col items-center gap-1.5 px-1 group ${onStageClick ? 'cursor-pointer' : 'cursor-default'}`}
                  title={`${stage.label}: ${isCompleted ? 'Passed' : isFailed ? 'Failed' : isCurrent ? 'In Progress' : 'Pending'}`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors
                      ${isCompleted ? 'bg-green-500 border-green-500 text-white' : ''}
                      ${isFailed ? 'bg-red-500 border-red-500 text-white' : ''}
                      ${isCurrent ? 'bg-blue-500 border-blue-500 text-white animate-pulse' : ''}
                      ${isUpcoming ? 'bg-muted border-border text-muted-foreground' : ''}
                      ${onStageClick ? 'group-hover:scale-110' : ''}
                    `}
                  >
                    {isCompleted ? (
                      <Icon name="Check" size={14} />
                    ) : isFailed ? (
                      <Icon name="X" size={14} />
                    ) : isCurrent ? (
                      <div className="w-2.5 h-2.5 rounded-full bg-white" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-border" />
                    )}
                  </div>
                  <span
                    className={`text-[11px] leading-tight text-center max-w-[76px] whitespace-normal
                      ${isCurrent ? 'text-blue-700 font-semibold' : isFailed ? 'text-red-700 font-semibold' : isCompleted ? 'text-green-700 font-medium' : 'text-muted-foreground'}
                    `}
                  >
                    {stage.label}
                  </span>
                </button>

                {index < STAGES.length - 1 && (
                  <div
                    className={`h-0.5 w-6 flex-shrink-0 mb-5 ${isCompleted ? 'bg-green-500' : isFailed ? 'bg-red-300' : 'bg-border'}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PipelineTracker;
export { STAGES };